import { connect } from 'react-redux';
import { has } from 'lodash';
import {
  PROBE_CREDENTIAL_REQUEST,
  GET_ALL_CREDENTIALS_REQUEST,
  PROBE_GET_TREE_REQUEST,
  GET_FULL_VENDOR_REQUEST,
  ADD_SINK_REQUEST,
  UPDATE_SINK_INFO_REQUEST,
} from 'js/constants/actionTypes';
import {
  ROUTE_MY_DATA_SETS,
  ROUTE_DATA_SETS_SHARED_WITH_ME,
  ROUTE_DESTINATIONS,
  ROUTE_REDESIGN,
} from 'js/constants/pathnames';
import { FORM_TYPE_EDIT_DESTINATION } from 'js/constants/formTypes';
import {
  loadDataCredentials,
  loadVendorAuths,
  loadDBDataTypes,
  loadDataSinkWithDataSet,
  fetchDataSet,
  fetchDataSamples,
  addCredentialFromForm,
  updateCredentialFromForm,
  verifyCredentialFromForm,
  verifyCredentialSilent,
  updateFormData,
  resetFormData,
  setFormStageIndex,
  markFormCompleted,
  updateDataSetInfo,
  updateSinkInfo,
  updateSinkTypeFromForm,
  updateSinkInfoFromForm,
  updateDataMap,
  probeGetConnectionTree,
  probeGetInitialDBTree,
  probeToggleTreeNode,
  probeClearAll,
  setFormError,
  setFormAwaitingValidation,
  loadFullVendor,
  setContextualSupport,
  unsetContextualSupport,
  setError,
  refreshPipeline,
} from 'js/actions';
import { getCurrentOrg, getCredentialsByType, getSamplesForDataSet } from 'js/reducers/selectors';
import UIUtils from 'js/lib/UIUtils';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import * as OLD_PATHS from 'js/constants/pathnames';
import DestinationSetup from './DestinationSetup.js';

const mapStateToProps = (state, ownProps) => {
  const { match = {} } = ownProps;

  const {
    entities: { dataSets, sinks, dataMaps, vendors, credentials: credentialEntities },
    forms,
    paging,
    credentials: { byId },
    dataSetSchemas,
    samples,
    vendors: {
      endpoints: { lastUpdated: vendorEndpointsLastUpdated },
    },
    probe,
    request,
    dbDataTypes: allDBDataTypes,
    routes: { previousPath },
    recipeBuilder,
  } = state;

  const urlIdParam = ownProps.id ?? UIUtils.extractParamFromURL(match);
  const { path } = match;
  const redesignPrefix = `${ROUTE_REDESIGN}${ROUTE_CONNECT}`;
  const urlPrefix = path?.startsWith?.(redesignPrefix) ? redesignPrefix : '';
  const dataSetIdParam = (() => {
    if (!ownProps.isEditMode && ownProps.isModal) {
      // nexsetId is now passed as a top-level prop (replacing the older
      // queryParams.nexsetId path). Keep the queryParams fallback for
      // backward compatibility with any caller still using the old shape.
      return ownProps.nexsetId ?? ownProps.queryParams?.nexsetId;
    }

    if (ownProps.isModal) {
      return null;
    }

    return path?.startsWith(`${urlPrefix}${ROUTE_MY_DATA_SETS}`) ||
      path?.startsWith(`${urlPrefix}${ROUTE_DATA_SETS_SHARED_WITH_ME}`)
      ? urlIdParam
      : null;
  })();
  const sinkIdParam = (() => {
    if (ownProps.isModal && ownProps.isEditMode) {
      return ownProps.id;
    }
    if (ownProps.isModal) {
      return null;
    }
    return path?.startsWith(`${urlPrefix}${ROUTE_DESTINATIONS}`) ? urlIdParam : null;
  })();

  const sink = sinkIdParam && sinks[sinkIdParam];
  const dataSet =
    (dataSetIdParam && dataSets[dataSetIdParam]) || (sink && dataSets[sink.data_set_id]);

  const dataSchema =
    dataSet && dataSetSchemas && dataSetSchemas[dataSet.id] && dataSetSchemas[dataSet.id].out;

  const dataMap = has(sink, 'data_map') && sink.data_map ? dataMaps[sink.data_map] : null;

  const form = forms[FORM_TYPE_EDIT_DESTINATION];
  const currentStage = form ? form.currentStage : 0;
  let formData = form ? form.data : null;

  let addedDataSinkId = sinkIdParam
    ? sinkIdParam
    : has(formData, 'addedDataSinkId')
      ? formData.addedDataSinkId
      : null;

  let toNested = has(formData, 'toNested') ? formData.toNested : null;

  let selectedConnection = has(formData, 'selectedConnection') ? formData.selectedConnection : null;

  let selectedCredential = has(formData, 'selectedCredential') ? formData.selectedCredential : null;

  let credentialCreationStatus = has(formData, 'credentialCreationStatus')
    ? formData.credentialCreationStatus
    : { message: null, credential: null };

  let credentialProbeStatus = has(formData, 'credentialProbeStatus')
    ? formData.credentialProbeStatus
    : { message: null, credential: null, isValid: false };

  let sinkConfig = has(formData, 'sinkConfig') && formData.sinkConfig ? formData.sinkConfig : {};

  let selectedBucket = has(formData, 'selectedBucket') ? formData.selectedBucket : null;

  if (!selectedBucket && sinkConfig?.database && sinkConfig?.collection) {
    selectedBucket = {
      collection: sinkConfig?.collection,
      database: sinkConfig?.database,
    };
  }

  let selectedTable = has(formData, 'selectedTable') ? formData.selectedTable : null;

  let selectedCollection = has(formData, 'selectedCollection') ? formData.selectedCollection : null;

  /**
   * @Why?
   * When a user creates a new collection, it may not exist in the database yet
   * until the flow executes. In this case, we construct the selectedCollection
   * object from the sinkConfig data to maintain UI consistency.
   */
  if (!selectedCollection && sinkConfig?.database && sinkConfig?.collection) {
    selectedCollection = {
      database: sinkConfig.database,
      id: `${sinkConfig.database}/${sinkConfig.collection}`,
      name: sinkConfig.collection,
    };
  }

  let isNewTable = has(formData, 'isNewTable') ? formData.isNewTable : null;
  let isNewCollection = has(formData, 'isNewCollection') ? formData.isNewCollection : null;

  let scriptCredentialsData = has(formData, 'scriptCredentialsData')
    ? formData.scriptCredentialsData
    : null;

  let selectedCredentialId =
    (selectedCredential && selectedCredential.id) ||
    (credentialCreationStatus && credentialCreationStatus.credential);

  const selectedCredentialInfo = has(formData, 'selectedCredentialInfo')
    ? formData.selectedCredentialInfo
    : null;

  let probeError = null;
  let probeData = null;
  let probeDataFlat = null;
  let probeTreeLastUpdatedAt = null;
  let topics;
  let queues;

  if (selectedCredentialId && has(probe, selectedCredentialId)) {
    const probeResponse = probe[selectedCredentialId];
    probeError = probeResponse.error;

    if (probeResponse.nestedProbeTree) {
      probeData = probeResponse.nestedProbeTree;
    } else if (probeResponse.topics) {
      topics = probeResponse.topics;
      queues = probeResponse.queues ? probeResponse.queues : [];
    }

    if (probeResponse.tree) {
      probeDataFlat = probeResponse.tree;
    }

    probeTreeLastUpdatedAt = probeResponse.treeLastUpdatedAt;
  }

  let isLoadingProbeData = !!request[PROBE_GET_TREE_REQUEST];
  let isLoadingCredentials = !!request[GET_ALL_CREDENTIALS_REQUEST];
  let isVerifyingCredential = !!request[PROBE_CREDENTIAL_REQUEST];
  let isCreatingSink = !!request[ADD_SINK_REQUEST];
  let isUpdatingSink = !!request[UPDATE_SINK_INFO_REQUEST];

  let addedDataSinkError = has(formData, 'addedDataSinkError') ? formData.addedDataSinkError : null;

  let updatedDataSinkId = has(formData, 'updatedDataSinkId') ? formData.updatedDataSinkId : null;

  let updatedDataSinkError = has(formData, 'updatedDataSinkError')
    ? formData.updatedDataSinkError
    : null;

  let dataMapSettings = has(formData, 'dataMapSettings') ? formData.dataMapSettings : null;

  let addedDataSink = addedDataSinkId ? sinks[addedDataSinkId] : null;
  let updatedDataSink = updatedDataSinkId ? sinks[updatedDataSinkId] : null;
  let sinkCredential = addedDataSink && credentialEntities[addedDataSink.data_credentials];

  let { dbDataTypes, schemaToDBDataTypes } = allDBDataTypes[selectedConnection] || {};

  const destinationDraft = recipeBuilder?.destinationDraft;

  const credentialsProbingId = state.credentials?.probingId ?? null;

  // get parent dataset samples for macro replacement in api_server flows
  const parentDataSetSamples = dataSet ? getSamplesForDataSet(state, { data: dataSet }) : null;

  return {
    sink,
    addedDataSink,
    addedDataSinkError,
    allCredentials: byId,
    loadingVendorInfo: request[GET_FULL_VENDOR_REQUEST],
    credentialCreationStatus,
    credentialEntities,
    credentialsProbingId,
    credentialProbeStatus,
    credentials: getCredentialsByType(state),
    currentOrg: getCurrentOrg(state),
    currentStage,
    dataMap,
    dataMapSettings,
    dataSchema,
    dataSet,
    dataSetIdParam,
    dbDataTypes,
    excludeByAttrName: formData && formData.excludeByAttrName,
    formData: formData || {},
    isLoadingCredentials,
    isLoadingProbeData,
    isNewCollection,
    isNewTable,
    isVerifyingCredential,
    orderedOutputSchema: formData && formData.orderedOutputSchema,
    paging,
    previousPath,
    probeData,
    probeDataFlat,
    probeError,
    probeTreeLastUpdatedAt,
    samples,
    schemaToDBDataTypes,
    scriptCredentialsData,
    selectedBucket,
    selectedCollection,
    selectedConnection,
    selectedCredential,
    selectedCredentialId,
    selectedCredentialInfo,
    selectedTable,
    sinkConfig,
    sinkCredential,
    sinkIdParam,
    toNested,
    topics,
    queues,
    updatedDataSink,
    updatedDataSinkError,
    vendorEndpointsLastUpdated,
    vendors,
    destinationDraft,
    isCreatingSink,
    isUpdatingSink,
    parentDataSet: dataSet,
    parentDataSetSamples,
  };
};

const DestinationSetupContainer = connect(mapStateToProps, {
  loadDataCredentials,
  loadFullVendor,
  loadVendorAuths,
  loadDBDataTypes,
  loadDataSinkWithDataSet,
  fetchDataSet,
  refreshPipeline,
  fetchDataSamples,
  addCredentialFromForm,
  updateCredentialFromForm,
  verifyCredentialFromForm,
  verifyCredentialSilent,
  updateFormData,
  resetFormData,
  setFormStageIndex,
  markFormCompleted,
  probeGetConnectionTree,
  probeGetInitialDBTree,
  probeToggleTreeNode,
  probeClearAll,
  updateDataSetInfo,
  updateSinkInfo,
  updateSinkTypeFromForm,
  updateSinkInfoFromForm,
  updateDataMap,
  setFormError,
  setFormAwaitingValidation,
  setContextualSupport,
  unsetContextualSupport,
})(DestinationSetup);

DestinationSetupContainer.screenConfig = {
  screenName: 'DestinationSetupContainer',
  domain: 'integrate',
  hideSideBar: true,
  pageTitle: 'Destination Setup',
  path: [
    `${ROUTE_REDESIGN}${ROUTE_CONNECT}${OLD_PATHS.ROUTE_MY_DATA_SETS}/:id/send`,
    `${ROUTE_REDESIGN}${ROUTE_CONNECT}${OLD_PATHS.ROUTE_DESTINATIONS}/:id/edit`,
    `${ROUTE_REDESIGN}${ROUTE_CONNECT}${OLD_PATHS.ROUTE_DATA_SETS_SHARED_WITH_ME}/:id/send`,
  ],
};

export default DestinationSetupContainer;
